/**
 * Member Activation → Archive (AR-328).
 *
 * Archived modules and steps keep their content, settings and history and are
 * excluded from the live flow; Restore returns an item to its previous position.
 */
import React, { useState } from 'react';
import { Layers, RotateCcw } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  PanelSectionLabel,
  TypeBadge,
} from '@/components/admin/activation/activationPrimitives';
import { ARCHIVED_MODULES, ARCHIVED_STEPS } from '@/lib/activationBuilder';

const MemberActivationArchivePage: React.FC = () => {
  const { toast } = useToast();
  const [modules, setModules] = useState(ARCHIVED_MODULES);
  const [steps, setSteps] = useState(ARCHIVED_STEPS);

  const restore = (name: string, kind: 'module' | 'step') => {
    if (kind === 'module') setModules((current) => current.filter((m) => m.name !== name));
    else setSteps((current) => current.filter((s) => s.name !== name));
    toast({
      title: `${kind === 'module' ? 'Module' : 'Step'} restored`,
      description: `“${name}” returned to its previous position in the live flow.`,
    });
  };

  const empty = !modules.length && !steps.length;

  return (
    <AdminLayout
      title="Archive"
      description="Archived modules and steps. Restore any item to return it to its previous position."
    >
      <p className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
        Archived items are hidden from members and excluded from KPI counts, but keep their content,
        settings, and history. Restore any item to return it to its previous position.
      </p>

      {empty ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-16 text-center dark:border-gray-700 dark:bg-gray-900/50">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Nothing is archived
          </p>
          <p className="mx-auto mt-1 max-w-md text-xs text-gray-500 dark:text-gray-400">
            Archiving a module or step from the builder moves it here, out of the live flow.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {!!modules.length && (
            <section>
              <PanelSectionLabel>Archived modules</PanelSectionLabel>
              <ul className="space-y-2">
                {modules.map((module) => (
                  <li
                    key={module.name}
                    className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                      <Layers className="h-3.5 w-3.5" />
                    </span>
                    <div className="min-w-0 flex-1 basis-56">
                      <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {module.name}
                      </p>
                      <p className="truncate text-[11px] text-gray-500 dark:text-gray-400">
                        {module.meta}
                      </p>
                    </div>
                    <Button variant="outline" size="sm">
                      Preview
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => restore(module.name, 'module')}
                      className="bg-emerald-600 text-white hover:bg-emerald-700"
                    >
                      <RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {!!steps.length && (
            <section>
              <PanelSectionLabel>Archived steps</PanelSectionLabel>
              <ul className="space-y-2">
                {steps.map((step) => (
                  <li
                    key={step.name}
                    className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900"
                  >
                    <TypeBadge type={step.type} />
                    <div className="min-w-0 flex-1 basis-56">
                      <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {step.name}
                      </p>
                      <p className="truncate text-[11px] text-gray-500 dark:text-gray-400">
                        {step.meta}
                      </p>
                    </div>
                    <Button variant="outline" size="sm">
                      Preview
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => restore(step.name, 'step')}
                      className="bg-emerald-600 text-white hover:bg-emerald-700"
                    >
                      <RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default MemberActivationArchivePage;
